import type { CrowdexUIMessage, CrowdexUIMessagePart } from '@crowdex/agent-contracts';
import {
  type CodeCallKind,
  codeRunOperations,
  codeStep,
  isCodeCallPart,
  isCodeRunPart,
} from './code-mode';
import type { MessagePartRenderItem } from './message-grouping';
import { terminalRunning } from './terminal';
import { dynamicToolActivityKind } from './tool-identity';
import type { DynamicToolPart, TerminalPart, ToolActivityPart } from './types';

/**
 * A turn's steps (design D12): its thinking, tool calls and the words it wrote between them fold
 * into one line, "Worked 6 min · 9 steps", that opens in place. What the turn shows the user (its
 * cards and its answer, the words after its last step) stays in the transcript, in order.
 */
export type TurnLayout = {
  /** What folds into the steps line, in transcript order. */
  steps: MessagePartRenderItem[];
  /** The parts of those steps, in order. */
  parts: CrowdexUIMessagePart[];
  /** What stays in the transcript, in order: cards, then the answer. */
  shown: MessagePartRenderItem[];
  /** Commands whose output reads as a test run: each also shows as a card. */
  testRuns: { tool: DynamicToolPart | null; terminal: TerminalPart; summary: string }[];
  /** How many steps the line counts: each thought and each tool call. */
  count: number;
  /** "4 files read · 2 edits · 3 commands", empty without tool calls. */
  summary: string;
  /** A step still runs (a tool, a thought). */
  running: boolean;
  /** A step waits for the user (an interrupted tool): the steps open by themselves. */
  attention: boolean;
};

const STEP_KINDS: ReadonlySet<MessagePartRenderItem['kind']> = new Set([
  'group',
  'single-activity',
  'tool-terminal',
]);

function itemParts(item: MessagePartRenderItem): CrowdexUIMessagePart[] {
  switch (item.kind) {
    case 'group':
      return item.parts;
    case 'single-activity':
    case 'standalone':
      return [item.part];
    case 'tool-terminal':
    case 'background-terminal':
      return [item.tool, item.terminal];
    case 'question':
      return [item.part];
    case 'sources':
    case 'tool-images':
      return item.parts;
  }
}

function isStepItem(item: MessagePartRenderItem): boolean {
  if (STEP_KINDS.has(item.kind)) return true;
  return item.kind === 'standalone' && item.part.type === 'reasoning';
}

function isTextItem(item: MessagePartRenderItem): boolean {
  return item.kind === 'standalone' && item.part.type === 'text';
}

/** A terminal's colour codes (ESC [ … m), left out of what is read. */
const ANSI_COLORS = new RegExp(`${String.fromCharCode(27)}\\[[0-9;]*m`, 'g');

/** A test runner's last summary line: "Tests 412 passed (412)", "3 failed, 9 passed". */
export function testRunSummary(output: string): string | null {
  const lines = output
    .split(/\r?\n/)
    .map((line) => line.replace(ANSI_COLORS, '').trim())
    .filter(Boolean);
  for (let index = lines.length - 1; index >= 0; index -= 1) {
    const line = lines[index] as string;
    if (/^tests?:?\s+\d+.*\b(passed|failed)\b/i.test(line)) return line.replace(/\s{2,}/g, ' ');
    if (/^=+ .*\b\d+ (passed|failed)\b.* =+$/i.test(line)) return line.replace(/^=+ | =+$/g, '');
    if (/^\d+ (passing|failing)\b/i.test(line)) return line;
  }
  return null;
}

function stepCount(parts: readonly CrowdexUIMessagePart[]): number {
  const calls = new Set<string>();
  // A cell's operations are part of its step.
  const { folded } = codeRunOperations(parts);
  let thoughts = 0;
  for (const part of parts) {
    if (part.type === 'reasoning') {
      if (part.text.trim() !== '' || part.state === 'streaming') thoughts += 1;
    } else if (part.type === 'dynamic-tool') {
      if (!folded.has(part.toolCallId)) calls.add(part.toolCallId);
    } else if (part.type === 'data-diff' || part.type === 'data-terminal')
      calls.add(part.data.toolCallId);
  }
  return calls.size + thoughts;
}

/** "4 files read · 2 edits · 3 commands": what the tool calls did, by kind. */
export function stepsSummary(parts: readonly CrowdexUIMessagePart[]): string {
  const tools = parts.filter((part): part is DynamicToolPart => part.type === 'dynamic-tool');
  const ids = (predicate: (part: DynamicToolPart) => boolean) =>
    new Set(tools.filter(predicate).map((part) => part.toolCallId));
  const terminals = new Set(
    parts.flatMap((part) => (part.type === 'data-terminal' ? [part.data.toolCallId] : [])),
  );
  const diffs = new Set(
    parts.flatMap((part) => (part.type === 'data-diff' ? [part.data.toolCallId] : [])),
  );
  const edits = new Set([
    ...ids((part) => dynamicToolActivityKind(part) === 'file-changes'),
    ...diffs,
  ]);
  // A tool with a terminal ran a command, whatever its name says ("pnpm --filter web test").
  const commands = new Set([
    ...ids((part) => dynamicToolActivityKind(part) === 'commands'),
    ...[...terminals].filter((id) => !edits.has(id)),
  ]);
  const reads = ids((part) => dynamicToolActivityKind(part) === 'exploration');
  const searches = ids(
    (part) => dynamicToolActivityKind(part) === 'web' && !commands.has(part.toolCallId),
  );
  // Code mode's cells count by what they call: a lookup, a show, the browser, an action.
  const operations = codeRunOperations(parts);
  const code = new Map<CodeCallKind, number>();
  for (const part of tools) {
    if (operations.folded.has(part.toolCallId) || !(isCodeRunPart(part) || isCodeCallPart(part)))
      continue;
    for (const kind of codeStep(part, operations.byRun.get(part.toolCallId) ?? []).kinds)
      code.set(kind, (code.get(kind) ?? 0) + 1);
  }
  const plural = (count: number, one: string, many: string) =>
    count === 0 ? [] : [`${count} ${count === 1 ? one : many}`];
  return [
    ...plural(reads.size, 'file read', 'files read'),
    ...plural(searches.size, 'search', 'searches'),
    ...plural(code.get('read') ?? 0, 'lookup', 'lookups'),
    ...plural(code.get('browser') ?? 0, 'browser step', 'browser steps'),
    ...plural(code.get('show') ?? 0, 'shown', 'shown'),
    ...plural(edits.size, 'edit', 'edits'),
    ...plural(code.get('write') ?? 0, 'action', 'actions'),
    ...plural(commands.size, 'command', 'commands'),
  ].join(' · ');
}

function itemRunning(
  part: CrowdexUIMessagePart,
  toolStates: ReadonlyMap<string, DynamicToolPart['state']>,
): boolean {
  if (part.type === 'reasoning') return part.state === 'streaming';
  // A tool waiting for the user's approval holds the turn: it still works.
  if (part.type === 'dynamic-tool')
    return (
      part.state === 'input-streaming' ||
      part.state === 'input-available' ||
      part.state === 'approval-requested'
    );
  // A command whose call ended ended too, even without an exit code.
  if (part.type === 'data-terminal')
    return terminalRunning(
      part.data.exitCode,
      part.data.signal,
      toolStates.get(part.data.toolCallId),
    );
  return false;
}

function toolStatesOf(
  items: readonly MessagePartRenderItem[],
): Map<string, DynamicToolPart['state']> {
  return new Map(
    items
      .flatMap(itemParts)
      .flatMap((part) =>
        part.type === 'dynamic-tool' ? [[part.toolCallId, part.state] as const] : [],
      ),
  );
}

function stepRuns(
  item: MessagePartRenderItem,
  toolStates: ReadonlyMap<string, DynamicToolPart['state']>,
): boolean {
  return isStepItem(item) && itemParts(item).some((part) => itemRunning(part, toolStates));
}

/**
 * Which items fold into the steps: every step, and once the turn is over the words written
 * before its last step (its commentary). While it works, what it says stays in view.
 */
function foldedItems(items: readonly MessagePartRenderItem[], working: boolean): boolean[] {
  let lastStep = -1;
  items.forEach((item, index) => {
    if (isStepItem(item)) lastStep = index;
  });
  return items.map(
    (item, index) => isStepItem(item) || (!working && isTextItem(item) && index < lastStep),
  );
}

/** Keeps an item in view: the images of steps that follow each other show as one strip. */
function pushShown(shown: MessagePartRenderItem[], item: MessagePartRenderItem): void {
  const previous = shown.at(-1);
  if (item.kind === 'tool-images' && previous?.kind === 'tool-images')
    shown[shown.length - 1] = { ...previous, parts: [...previous.parts, ...item.parts] };
  else shown.push(item);
}

function stepsLayout(
  steps: MessagePartRenderItem[],
  shown: MessagePartRenderItem[],
  running: boolean,
  interruptedError: string | undefined,
): TurnLayout {
  const stepParts = steps.flatMap(itemParts);
  const testRuns: TurnLayout['testRuns'] = [];
  for (const item of steps) {
    const pairs: { tool: DynamicToolPart | null; terminal: TerminalPart }[] =
      item.kind === 'tool-terminal'
        ? [{ tool: item.tool, terminal: item.terminal }]
        : itemParts(item).flatMap((part) =>
            part.type === 'data-terminal'
              ? [
                  {
                    tool:
                      stepParts.find(
                        (candidate): candidate is DynamicToolPart =>
                          candidate.type === 'dynamic-tool' &&
                          candidate.toolCallId === part.data.toolCallId,
                      ) ?? null,
                    terminal: part,
                  },
                ]
              : [],
          );
    for (const pair of pairs) {
      if (pair.terminal.data.exitCode === null) continue;
      const summary = testRunSummary(pair.terminal.data.output);
      if (summary !== null) testRuns.push({ ...pair, summary });
    }
  }
  const toolParts = stepParts.filter(
    (part): part is ToolActivityPart =>
      part.type === 'dynamic-tool' || part.type === 'data-diff' || part.type === 'data-terminal',
  );
  return {
    steps,
    parts: stepParts,
    shown,
    testRuns,
    count: stepCount(stepParts),
    summary: stepsSummary(toolParts),
    running,
    attention:
      interruptedError !== undefined &&
      stepParts.some(
        (part) =>
          part.type === 'dynamic-tool' &&
          part.state === 'output-error' &&
          part.errorText === interruptedError,
      ),
  };
}

/**
 * Splits a message's render plan into its steps and what it shows. Once the turn is over, its
 * answer is the text after its last step, and the text written between steps is commentary that
 * folds with them; while it works, what it says stays in view.
 */
export function turnLayout(
  items: readonly MessagePartRenderItem[],
  options: { interruptedError?: string; streaming?: boolean } = {},
): TurnLayout {
  const toolStates = toolStatesOf(items);
  const working = options.streaming === true || items.some((item) => stepRuns(item, toolStates));
  const folded = foldedItems(items, working);
  const steps: MessagePartRenderItem[] = [];
  const shown: MessagePartRenderItem[] = [];
  items.forEach((item, index) => {
    if (folded[index]) steps.push(item);
    else pushShown(shown, item);
  });
  return stepsLayout(steps, shown, working, options.interruptedError);
}

/**
 * A turn in transcript order: its runs of steps, each folded into its own line followed by the
 * cards its steps made (`layout.shown`), and the words between the runs where they were written.
 */
export type TurnSegment =
  | {
      kind: 'steps';
      key: string;
      layout: TurnLayout;
      /** The turn's last run of steps: its line says where the whole turn stands. */
      last: boolean;
    }
  | { kind: 'item'; item: MessagePartRenderItem };

function hasWords(item: MessagePartRenderItem): boolean {
  return item.kind === 'standalone' && item.part.type === 'text' && item.part.text.trim() !== '';
}

/**
 * Lays a turn out in transcript order (design D12). Steps that follow each other fold into one
 * line where they ran, and the words the turn wrote end that run: while it works, "text, steps,
 * text, steps, text" reads in that order, not every step above every word. Once it is over its
 * commentary folds too, so its steps make one line before its answer (as `turnLayout` splits it).
 * A card a run's steps made (a plan, images, a subagent) shows right after the run's line.
 */
export function turnSegments(
  items: readonly MessagePartRenderItem[],
  options: { interruptedError?: string; streaming?: boolean } = {},
): TurnSegment[] {
  const toolStates = toolStatesOf(items);
  const working = options.streaming === true || items.some((item) => stepRuns(item, toolStates));
  const folded = foldedItems(items, working);
  const segments: TurnSegment[] = [];
  let steps: MessagePartRenderItem[] = [];
  let shown: MessagePartRenderItem[] = [];
  const close = () => {
    // Keyed by its first step, not its words: a run keeps its line (open or not) once the turn
    // is over and its commentary folds in front of that step.
    const first = steps.find(isStepItem) ?? steps[0];
    if (first === undefined) return;
    segments.push({
      kind: 'steps',
      key: `${first.key}-steps`,
      layout: stepsLayout(
        steps,
        shown,
        steps.some((item) => stepRuns(item, toolStates)),
        options.interruptedError,
      ),
      last: false,
    });
    steps = [];
    shown = [];
  };
  items.forEach((item, index) => {
    if (folded[index]) steps.push(item);
    else {
      if (hasWords(item)) close();
      if (steps.length === 0) segments.push({ kind: 'item', item });
      else pushShown(shown, item);
    }
  });
  close();
  const lastRun = segments.findLastIndex((segment) => segment.kind === 'steps');
  const last = segments[lastRun];
  // The last run's line is the turn's activity line: it works while the turn works.
  if (last?.kind === 'steps')
    segments[lastRun] = { ...last, last: true, layout: { ...last.layout, running: working } };
  return segments;
}

/** When the turn started and ended, as its agent stamped them (`metadata.startedAt`…). */
export function turnTimes(message: CrowdexUIMessage): { start: number; end: number | null } | null {
  const metadata = (message.metadata ?? {}) as Record<string, unknown>;
  const start =
    typeof metadata['startedAt'] === 'string' ? Date.parse(metadata['startedAt']) : Number.NaN;
  if (Number.isNaN(start)) return null;
  const end =
    typeof metadata['finishedAt'] === 'string' ? Date.parse(metadata['finishedAt']) : Number.NaN;
  return { start, end: Number.isNaN(end) ? null : end };
}

/** "12 s", "6 min", "1 h 5 min". */
export function formatDuration(milliseconds: number): string {
  const seconds = Math.max(0, Math.round(milliseconds / 1000));
  if (seconds < 60) return `${seconds} s`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`;
}

/**
 * Where a turn stands, as its one activity line says it: still working, waiting on the user (a
 * tool asks for approval, a question waits for its answer), stopped by the user, or done.
 */
export type TurnActivity = 'working' | 'waiting' | 'stopped' | 'done';

/** A tool of the turn asks the user: an approval, or a question not answered yet. */
export function turnWaitsOnUser(items: readonly MessagePartRenderItem[]): boolean {
  return items.some((item) =>
    itemParts(item).some(
      (part) =>
        part.type === 'dynamic-tool' &&
        (part.state === 'approval-requested' ||
          (item.kind === 'question' && part.state === 'input-available')),
    ),
  );
}

/** Where the turn stands (see `TurnActivity`). */
export function turnActivity(input: {
  /** Still streaming, or a step still runs. */
  active: boolean;
  waiting: boolean;
  stopped: boolean;
}): TurnActivity {
  if (input.waiting) return 'waiting';
  if (input.active) return 'working';
  return input.stopped ? 'stopped' : 'done';
}

/**
 * The turn's activity line: "Working 3 min · 5 steps", "Waits on you · 5 steps", "Stopped · 2
 * steps", "Worked 6 min · 9 steps", and "Working" alone before its first step.
 */
export function stepsLabel(input: {
  activity: TurnActivity;
  count: number;
  /** How long the turn ran (so far), when known. */
  duration: number | null;
}): string {
  // Before its first step the line says only that the turn works; "0 s" says nothing either.
  const timed = (verb: string) =>
    input.duration === null || input.duration < 1000 || input.count === 0
      ? verb
      : `${verb} ${formatDuration(input.duration)}`;
  const head =
    input.activity === 'working'
      ? timed('Working')
      : input.activity === 'waiting'
        ? 'Waits on you'
        : input.activity === 'stopped'
          ? 'Stopped'
          : timed('Worked');
  if (input.count === 0) return head;
  return `${head} · ${input.count} ${input.count === 1 ? 'step' : 'steps'}`;
}

/** How long a test run took, as its runner printed it ("Duration 42.10s" → "42 s"). */
export function testRunDuration(output: string): string | null {
  const clean = output.replace(ANSI_COLORS, '');
  const match = /\bDuration\s+([\d.]+)\s*(ms|s|m)\b/i.exec(clean);
  if (match === null) return null;
  const value = Number(match[1]);
  const unit = match[2]?.toLowerCase();
  return formatDuration(unit === 'ms' ? value : unit === 'm' ? value * 60_000 : value * 1000);
}
